import { useSession } from '@airship-platform/airship-auth';
import { Middleware } from '@airship-platform/http-service';
import { PropsWithChildren, useRef } from 'react';
import { captureHttpError, createHttpClient } from '../services';
import { HttpProvider } from './HttpContext';

interface HttpAuthContextProps {
  options: { apiBaseUrl: string };
}

export const HttpAuthProvider: React.FC<
  PropsWithChildren<HttpAuthContextProps>
> = ({ children, options }) => {
  const { refreshSession, signOut, buildSignInUrl } = useSession();

  const buildMiddleware: () => Middleware = () => {
    const logoutAndRedirect = () => {
      signOut();
      window.location.replace(buildSignInUrl(window.location.href));
    };

    return {
      request: async (config) => {
        if (!('Authorization' in config.headers)) {
          const idToken = await refreshSession().then(
            (tokens) => tokens?.idToken
          );
          if (!idToken) {
            logoutAndRedirect();
            return config;
          }

          return Object.assign(config, {
            headers: {
              ...config.headers,
              Authorization: `Bearer ${idToken}`,
            },
          });
        }

        return config;
      },
      errorHandler: (error) => {
        const status = error.response?.status || error.status || 0;

        captureHttpError(error);

        if ([401, 403].includes(status)) {
          logoutAndRedirect();
        }
      },
    };
  };

  const httpRef = useRef(
    createHttpClient({
      baseURL: options.apiBaseUrl,
      middleware: buildMiddleware(),
    })
  );

  return <HttpProvider httpClient={httpRef.current}>{children}</HttpProvider>;
};
